import React, { useEffect, useState } from "react";
import { ArrowLeftRight, Briefcase, RotateCcw, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { deleteTransaction, setTransactionScope, setTransfer, updateCategory } from "@/lib/api";
import { categoryColor, formatSigned, formatTime, relativeDay } from "@/lib/format";

const SOURCE_LABELS = {
  csv: "Import CSV",
  enablebanking: "Banca",
  paypal: "PayPal",
  cash: "Contanti",
  demo: "Demo",
};

export const TransactionDetailModal = ({ tx, categories, onClose, onUpdated, onTransferred, onDeleted }) => {
  const [selected, setSelected] = useState(tx?.category || "");
  const [scope, setScope] = useState(tx?.scope || "personal");
  const [scopeSaving, setScopeSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transfering, setTransfering] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setSelected(tx?.category || "");
    setScope(tx?.scope || "personal");
  }, [tx]);

  const isTransfer = !!(tx?.transfer || tx?.excluded);
  const isCash = tx?.source === "cash";

  const removeCash = async () => {
    setDeleting(true);
    try {
      await deleteTransaction(tx.id);
      toast.success("Movimento in contanti eliminato.");
      onDeleted?.(tx.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const updated = await updateCategory(tx.id, selected);
      toast.success("Categoria aggiornata. L'agente imparerà da questa correzione.");
      onUpdated(updated);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const changeScope = async (next) => {
    if (next === scope || scopeSaving) return;
    const prev = scope;
    setScope(next);
    setScopeSaving(true);
    try {
      const updated = await setTransactionScope(tx.id, next);
      toast.success(next === "business" ? "Segnato come spesa di lavoro." : "Segnato come spesa personale.");
      onUpdated?.(updated);
    } catch (e) {
      setScope(prev);
      toast.error(e?.response?.data?.detail || "Errore durante l'aggiornamento");
    } finally {
      setScopeSaving(false);
    }
  };

  const toggleTransfer = async () => {
    const next = !isTransfer;
    setTransfering(true);
    try {
      await setTransfer(tx.id, next);
      toast.success(
        next
          ? "Segnato come giroconto: escluso da spese ed entrate."
          : "Ripristinato come movimento normale."
      );
      onTransferred?.(tx.id, next);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'aggiornamento");
    } finally {
      setTransfering(false);
    }
  };

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[380px] rounded-[12px] border-white/[0.08] bg-[#0D0D14] p-0 text-white"
        data-testid="transaction-detail-modal"
      >
        {tx && (
          <>
            <DialogHeader className="px-5 pb-0 pt-5 text-left">
              <DialogTitle className="pr-6 text-sm font-normal leading-6 text-white/90">{tx.description}</DialogTitle>
              <DialogDescription className="caps-tag pt-1 !text-white/35">
                {relativeDay(tx.date)} · {formatTime(tx.date)} · {SOURCE_LABELS[tx.source] || "Movimento"}
              </DialogDescription>
            </DialogHeader>
            <div className="px-5 pb-5">
              <p
                className={`mono text-[40px] font-light tracking-[-1.5px] tabular ${tx.amount > 0 ? "text-[#00FF87]" : "text-white"}`}
                data-testid="transaction-detail-amount"
              >
                {formatSigned(tx.amount)}
              </p>

              <div className="mt-6">
                <p className="caps-tag text-white/35">Categoria</p>
                <div className="mt-2">
                  <Select value={selected} onValueChange={setSelected} disabled={isTransfer}>
                    <SelectTrigger
                      className="h-11 w-full rounded-[8px] border-white/[0.12] bg-[#050508] text-sm font-light text-white focus:ring-[#00FF87]/30 disabled:opacity-40"
                      data-testid="transaction-category-select"
                    >
                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5" style={{ background: categoryColor(selected) }} />
                        <SelectValue placeholder="Scegli categoria" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-64 rounded-[8px] border-white/[0.08] bg-[#10101A] text-white">
                      {categories.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`category-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                          <span className="flex items-center gap-2 font-light">
                            <span className="h-1.5 w-1.5" style={{ background: categoryColor(c) }} />
                            {c}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="mt-2 text-[11px] font-light leading-4 text-white/30">
                  Correggendo la categoria insegni all'agente a classificare meglio.
                </p>
              </div>

              {!isTransfer && (
                <button
                  onClick={confirm}
                  disabled={saving || !selected || selected === tx.category}
                  data-testid="transaction-category-confirm-button"
                  className="mt-5 h-11 w-full rounded-[6px] border border-[#00FF87]/40 bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] text-[#00FF87] hover:bg-[#00FF87]/[0.06] active:scale-[0.98] disabled:border-white/[0.1] disabled:text-white/25"
                >
                  {saving ? "Salvataggio…" : "Conferma categoria"}
                </button>
              )}

              {/* Business / Personale */}
              {!isTransfer && (
                <div className="mt-5 border-t border-white/[0.06] pt-4">
                  <p className="caps-tag text-white/35">Tipo di spesa</p>
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="transaction-scope-toggle">
                    <button
                      onClick={() => changeScope("business")}
                      disabled={scopeSaving}
                      data-testid="scope-business-button"
                      className={`flex h-10 items-center justify-center gap-1.5 rounded-[6px] border text-[11px] font-medium uppercase tracking-[0.14em] transition-colors duration-150 active:scale-[0.98] disabled:opacity-50 ${
                        scope === "business"
                          ? "border-[#F5A623]/50 bg-[#F5A623]/[0.08] text-[#F5A623]"
                          : "border-white/[0.1] bg-transparent text-white/45 hover:bg-white/[0.04]"
                      }`}
                    >
                      <Briefcase size={13} />
                      Business
                    </button>
                    <button
                      onClick={() => changeScope("personal")}
                      disabled={scopeSaving}
                      data-testid="scope-personal-button"
                      className={`flex h-10 items-center justify-center gap-1.5 rounded-[6px] border text-[11px] font-medium uppercase tracking-[0.14em] transition-colors duration-150 active:scale-[0.98] disabled:opacity-50 ${
                        scope === "personal"
                          ? "border-white/40 bg-white/[0.06] text-white/90"
                          : "border-white/[0.1] bg-transparent text-white/45 hover:bg-white/[0.04]"
                      }`}
                    >
                      <User size={13} />
                      Personale
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] font-light leading-4 text-white/30">
                    Separa le spese di lavoro da quelle personali per la contabilità e i report.
                  </p>
                </div>
              )}

              {/* Movimento in contanti: eliminabile */}
              {isCash && (
                <div className="mt-5 border-t border-white/[0.06] pt-4">
                  <p className="caps-tag text-white/35">Movimento manuale</p>
                  <p className="mt-1.5 text-[11px] font-light leading-4 text-white/30">
                    Inserito da te in contanti. Puoi eliminarlo: il saldo verrà ricalcolato.
                  </p>
                  <button
                    onClick={removeCash}
                    disabled={deleting}
                    data-testid="transaction-delete-button"
                    className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-[#FF4D4D]/35 bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] text-[#FF6B6B] hover:bg-[#FF4D4D]/[0.06] active:scale-[0.98] disabled:opacity-40"
                  >
                    <Trash2 size={13} />
                    {deleting ? "Eliminazione…" : "Elimina movimento"}
                  </button>
                </div>
              )}

              {/* Giroconto: trasferimento tra conti propri -> escluso da spese/entrate */}
              {!isCash && (
              <div className="mt-5 border-t border-white/[0.06] pt-4">
                {isTransfer ? (
                  <>
                    <p className="text-[11px] font-light leading-4 text-white/45" data-testid="transaction-transfer-note">
                      Questo movimento è segnato come <span className="text-white/75">giroconto</span> ed è escluso da spese, entrate e insight.
                    </p>
                    <button
                      onClick={toggleTransfer}
                      disabled={transfering}
                      data-testid="transaction-restore-button"
                      className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-white/[0.15] bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] text-white/85 hover:bg-white/[0.05] active:scale-[0.98] disabled:opacity-40"
                    >
                      <RotateCcw size={13} />
                      {transfering ? "Ripristino…" : "Ripristina come spesa"}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="caps-tag text-white/35">Giroconto</p>
                    <p className="mt-1.5 text-[11px] font-light leading-4 text-white/30">
                      È un trasferimento tra i tuoi conti (es. ricarica Revolut)? Escludilo: non verrà conteggiato come spesa o entrata.
                    </p>
                    <button
                      onClick={toggleTransfer}
                      disabled={transfering}
                      data-testid="transaction-mark-transfer-button"
                      className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-white/[0.15] bg-transparent text-[11px] font-medium uppercase tracking-[0.2em] text-white/85 hover:bg-white/[0.05] active:scale-[0.98] disabled:opacity-40"
                    >
                      <ArrowLeftRight size={13} />
                      {transfering ? "Salvataggio…" : "Segna come giroconto"}
                    </button>
                  </>
                )}
              </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
